/**
 * Conservative byte ceiling for a launch POST body. Sits below Railway's
 * edge request-size cap so we fail loud on the client with a clear message
 * instead of getting silently rejected upstream of the tRPC handler and
 * surfacing as `TRPCClientError: Unknown error`.
 */
export const LAUNCH_PAYLOAD_SOFT_LIMIT_BYTES = 900_000;

/**
 * Minimal structural types describing only the fields this filter cares
 * about. The launch-mutation input is inferred by tRPC at the call site;
 * the filter preserves that broader type via generics (`P extends
 * LaunchPayloadShape`) while reaching into the narrow `thumbnailUrl`-
 * bearing structure to strip it.
 */
interface PlacementVariant {
  thumbnailUrl?: string;
}

interface CarouselCard {
  thumbnailUrl?: string;
  placementVariants?: PlacementVariant[];
}

interface CarouselItem {
  carouselCards: CarouselCard[];
}

interface LaunchPayloadShape {
  // `object`, not an all-optional interface: TypeScript's weak-type check
  // would otherwise reject asset rows that happen to carry no thumbnail.
  assetCopyData?: object[];
  carouselCards?: CarouselCard[];
  carousels?: CarouselItem[];
}

function stripCard<T extends CarouselCard>(card: T): T {
  const { thumbnailUrl: _thumbnailUrl, placementVariants, ...rest } = card;
  const base = rest as T;
  if (!placementVariants) return base;
  return {
    ...base,
    placementVariants: placementVariants.map((variant) => {
      const { thumbnailUrl: _variantThumbnailUrl, ...variantRest } = variant;
      return variantRest as PlacementVariant;
    }),
  };
}

/**
 * Removes `thumbnailUrl` from asset and carousel entries before the launch
 * POST goes out. The backend schema accepts the field so inferred types on
 * the client can carry it, but nothing server-side reads it — it exists
 * purely for the in-form preview rendering. Each thumbnail is a ~100KB
 * base64 data URL, so a 16-asset batch can easily push a launch payload
 * past the proxy's request-body limit.
 */
export function stripThumbnailsFromLaunchPayload<P extends LaunchPayloadShape>(
  payload: P,
): P {
  return {
    ...payload,
    assetCopyData: payload.assetCopyData?.map((asset) => {
      const { thumbnailUrl: _thumbnailUrl, ...rest } = asset as object & {
        thumbnailUrl?: string;
      };
      return rest;
    }),
    carouselCards: payload.carouselCards?.map((card) => stripCard(card)),
    carousels: payload.carousels?.map((carousel) => ({
      ...carousel,
      carouselCards: carousel.carouselCards.map((card) => stripCard(card)),
    })),
  };
}

/**
 * Throws an actionable error if the serialized payload exceeds the soft
 * limit. Run after stripping thumbnails so the message reflects what's
 * actually being sent.
 */
export function assertLaunchPayloadSize<P extends LaunchPayloadShape>(
  payload: P,
): void {
  const serialized = JSON.stringify(payload);
  const bytes =
    typeof Blob !== "undefined"
      ? new Blob([serialized]).size
      : serialized.length;
  if (bytes <= LAUNCH_PAYLOAD_SOFT_LIMIT_BYTES) return;

  const mb = (bytes / 1_000_000).toFixed(2);
  const limitMb = (LAUNCH_PAYLOAD_SOFT_LIMIT_BYTES / 1_000_000).toFixed(2);
  throw new Error(
    `Launch payload is ${mb} MB, exceeding the ${limitMb} MB request limit. ` +
      `Try launching with fewer assets in this batch, or contact support if this persists.`,
  );
}
